import React, { useState, useRef, useEffect } from "react";
import { API } from "aws-amplify";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import DOMPurify from 'dompurify';
import { getConversationChatPublicAssistant } from "../../../../graphql/queries";
import {
  ChatContainer,
  ChatHeader,
  MessagesContainer,
  MessageBubble,
  MessageWrapper,
  MessageHeader,
  MessageAvatar,
  MessageTime,
  InputContainer,
  InputWrapper,
  Input,
  SendButton,
  AttachButton,
  ChatToggleButton,
  ChatWindow
} from './ChatBox.styled';
import { Icon } from '../../../../elements';

const MessageAvatarWithIcon = ({ type }) => (
  <MessageAvatar type={type}>
    <Icon 
      name={type === 'query' ? 'userAvatar' : 'annBrainIcon'} 
      width="32px" 
      height="32px"
      dataTestId={`avatar-${type}`}
    />
  </MessageAvatar>
);

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' bytes';
  else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  else return (bytes / 1048576).toFixed(1) + ' MB';
};

const ChatBox = ({
  agentName = "Ann Brooks", 
  agentRole = "Customer Support", 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const recentFileMessage = [...messages].reverse().find(msg => msg.type === 'file');
    const hasRecentFileUpload = recentFileMessage &&
      messages.indexOf(recentFileMessage) > messages.length - 3;

    const userMessage = {
      type: 'query',
      content: inputValue,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      hasAttachment: hasRecentFileUpload,
      fileReference: hasRecentFileUpload ? recentFileMessage.files.map(f => f.name) : null
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const messagePayload = {
        message: inputValue,
        thread_id: '',
        query_id: '',
        streaming: true
      };

      if (hasRecentFileUpload) {
        messagePayload.files = recentFileMessage.files.map(f => f.name).join(', ');
      }

      const response = await API.graphql({
        query: getConversationChatPublicAssistant,
        variables: {
          input: messagePayload
        },
        authMode: "API_KEY",
      });

      if (response.data?.getConversationChatPublicAssistant?.statusCode === "200") {
        const responseBody = response.data.getConversationChatPublicAssistant.body;
        let parsedBody;
        
        try {
          parsedBody = typeof responseBody === 'string' ? JSON.parse(responseBody) : responseBody;
        } catch (e) {
          console.log('Response body parsing error:', e);
          parsedBody = { answer: responseBody };
        }
        
        if (parsedBody.answer) {
          const agentResponse = {
            type: 'response',
            content: parsedBody.answer,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            thread_id: response.data?.getConversationChatPublicAssistant?.thread_id || parsedBody.thread_id
          };
          
          setMessages(prev => [...prev, agentResponse]);
        } else {
          throw new Error('Invalid response format from assistant');
        }
      } else {
        throw new Error(`Request failed with status code ${response.data?.getConversationChatPublicAssistant?.statusCode}`);
      }
    } catch (error) {
      console.error('Chat error:', error);
      let errorMessage = 'An error occurred while processing your message.';
      
      if (error.errors?.[0]?.message) {
        errorMessage = error.errors[0].message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      const errorResponse = {
        type: 'response',
        content: errorMessage,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      
      setMessages(prev => [...prev, errorResponse]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      console.log('Selected files:', files);

      const fileAttachments = Array.from(files).map(file => ({
        name: file.name,
        type: file.type || 'Unknown type',
        size: formatFileSize(file.size)
      }));

      const fileMessage = {
        type: 'file',
        content: `Attached ${files.length > 1 ? `${files.length} files` : 'file'}:`,
        files: fileAttachments,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      setMessages(prev => [...prev, fileMessage]);
      setInputValue(`Please analyze the attached ${files.length > 1 ? 'files' : 'file'}.`);
      event.target.value = null;
    }
  };

  const renderFileAttachment = (files) => {
    return (
      <div className="file-attachments">
        {files.map((file, index) => {
          let iconName = "file";
          if (file.type.includes('pdf')) {
            iconName = "fileV2";
          } else if (file.type.includes('word') || file.type.includes('document')) {
            iconName = "wordLogo";
          } else if (file.type.includes('spreadsheet') || file.type.includes('excel')) {
            iconName = "excelLogo";
          }

          return (
            <div key={index} className="file-attachment">
              <Icon name={iconName} width="22px" height="22px" />
              <div className="file-info">
                <div className="file-name">{file.name}</div>
                <div className="file-meta">
                  {file.type.split('/').pop()} • {file.size}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <ChatWindow $isOpen={isOpen}>
        <ChatHeader>
          <div className="agent-info">
            <Icon name="annBrainIcon" width="32px" height="32px" />
            <div>
              <div className="agent-name">{agentName}</div>
              <div className="agent-role">{agentRole}</div>
            </div>
          </div>
        </ChatHeader>

        <MessagesContainer>
          {messages.length === 0 && (
            <MessageWrapper>
              <MessageHeader>
                <MessageAvatarWithIcon type="response" />
                <MessageTime>Now</MessageTime>
              </MessageHeader>
              <MessageBubble type="response">
                Hi! I'm {agentName}, your virtual assistant. How can I help you today?
              </MessageBubble>
            </MessageWrapper>
          )}
          {messages.map((message, index) => (
            <MessageWrapper key={index}>
              <MessageHeader>
                <MessageAvatarWithIcon type={message.type === 'file' ? 'query' : message.type} />
                <MessageTime>{message.timestamp}</MessageTime>
              </MessageHeader>
              <MessageBubble type={message.type === 'file' ? 'query' : message.type}>
                {message.type === 'response' ? (
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message.content) }} />
                ) : message.type === 'file' ? (
                  <>
                    <div className="file-message">{message.content}</div>
                    {renderFileAttachment(message.files)}
                  </>
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message.content) }} />
                )}
              </MessageBubble>
            </MessageWrapper>
          ))}
          {isLoading && (
            <MessageWrapper>
              <MessageHeader>
                <MessageAvatarWithIcon type="response" />
                <MessageTime>Now</MessageTime>
              </MessageHeader>
              <MessageBubble type="response">
                <div className="typing-indicator">Typing</div>
              </MessageBubble>
            </MessageWrapper>
          )}
          <div ref={messagesEndRef} />
        </MessagesContainer>

        <InputContainer>
          <InputWrapper>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
              multiple
            />
            <AttachButton onClick={handleFileClick}>
              <Icon name="clip" width="24px" height="24px" />
            </AttachButton>
            <Input
              as="textarea"
              placeholder="Type your message..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
              rows={1}
              disabled={isLoading}
            />
            <SendButton 
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
            >
              <Icon name="send" width="24px" height="24px" />
            </SendButton>
          </InputWrapper>
        </InputContainer>
      </ChatWindow>
    </>
  );
};

export default ChatBox;
